import {Component, inject, signal, viewChild} from '@angular/core';
import {saveAs} from 'file-saver';
import {CommonModule} from '@angular/common';
import {DxDataGridComponent, DxDataGridModule, DxToolbarModule} from 'devextreme-angular';
import {SharedModule} from '../../../shared/shared.module';
import {FactureExterneService} from '../../../core/services/entities/facture-externe.service';
import {FactureExterneDTO} from '../../../core/dtos/facturation/fournisseurs/factures/facture-externe-dto';
import {ButtonThemeEnum} from '../../../shared/ui/button/button-theme-enum';
import {FactureExterneDialogComponent} from './facture-externe-dialog/facture-externe-dialog.component';
import {CegidExportErrorsDialogComponent} from '../cegid/cegid-export-errors-dialog/cegid-export-errors-dialog.component';
import {CegidExportErrorDTO} from '../../../core/dtos/facturation/fournisseurs/cegid/cegid-export-error-dto';
import {FactureExterneStatutEnum, FACTURE_EXTERNE_STATUT_LABELS} from "../../../core/enums/facturation/fournisseur/facture-externe-statut-enum";
import {RapprochementFactureExterneStatutEnum, RAPPROCHEMENT_FACTURE_EXTERNE_STATUT_LABELS} from "../../../core/enums/facturation/fournisseur/rapprochement-facture-externe-statut-enum";
import {Auth2Service} from '../../../core/services/security/auth2.service';
import {UtilsService} from '../../../core/utils/utils.service';
import {ToastService} from '../../../core/services/technique/toast.service';
import {HAS_EXPORT_CEGID, MSG_KEY, MSG_SEVERITY} from '../../../core/constants';

@Component({
  selector: 'yo-factures-externes',
  templateUrl: './factures-externes.component.html',
  styleUrls: ['./factures-externes.component.scss'],
  standalone: true,
  imports: [CommonModule, DxDataGridModule, DxToolbarModule, SharedModule, FactureExterneDialogComponent, CegidExportErrorsDialogComponent],
  providers: [FactureExterneService]
})
export class FacturesExternesComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;

  readonly statutList = Object.values(FactureExterneStatutEnum).map(value => ({
    value,
    label: FACTURE_EXTERNE_STATUT_LABELS[value]
  }));

  readonly rapprochementStatutList = Object.values(RapprochementFactureExterneStatutEnum).map(value => ({
    value,
    label: RAPPROCHEMENT_FACTURE_EXTERNE_STATUT_LABELS[value]
  }));

  readonly factureExterneSvc = inject(FactureExterneService);
  private readonly auth2Svc = inject(Auth2Service);
  private readonly utilsSvc = inject(UtilsService);
  private readonly toastSvc = inject(ToastService);

  readonly hasExportCegid: boolean = this.auth2Svc.hasFeature(this.auth2Svc.utilisateur, HAS_EXPORT_CEGID);

  readonly dateReceptionSelectedDates: Date[] = [];

  readonly showDialog = signal(false);
  readonly selectedFactureId = signal<number | null>(null);
  readonly selectedDialogTitle = signal('Facture externe');
  readonly selectedFactureIdList = signal<number[]>([]);
  readonly showCegidErrorsDialog = signal(false);
  readonly cegidErrorList = signal<CegidExportErrorDTO[]>([]);


  private readonly grid = viewChild.required(DxDataGridComponent);
  private filterDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  getStatutLabel = (rowData: FactureExterneDTO): string =>
    FACTURE_EXTERNE_STATUT_LABELS[rowData.statut as FactureExterneStatutEnum] ?? rowData.statut;

  getRapprochementStatutLabel = (rowData: FactureExterneDTO): string =>
    RAPPROCHEMENT_FACTURE_EXTERNE_STATUT_LABELS[rowData.rapprochementStatut as RapprochementFactureExterneStatutEnum] ?? rowData.rapprochementStatut;

  getNumeroBLList = (data: FactureExterneDTO): string[] => {
    if (!data?.rapprochementList?.length) return [];
    return data.rapprochementList
      .map(r => r.numeroBonLivraison)
      .filter(n => n?.trim());
  };

  calculateNumeroBLFilterExpression = (filterValue: string): any => {
    if (!filterValue) return null;
    const val = filterValue.toLowerCase();
    return (rowData: FactureExterneDTO) =>
      (rowData.rapprochementList ?? []).some(r => r.numeroBonLivraison?.toLowerCase().includes(val));
  };

  exportCegid = (): void => {
    const factureIdList = this.selectedFactureIdList();
    const filters = factureIdList.length ? { factureIdList } : {};
    this.factureExterneSvc.exportCegidFactureAvoir(filters).subscribe(result => {
      if (result?.erreurList?.length) {
        this.cegidErrorList.set(result.erreurList);
        this.showCegidErrorsDialog.set(true);
        return;
      }
      if (!result?.fichier) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, 'Aucun élément à exporter');
        return;
      }
      const byteCharacters = atob(result.fichier);
      const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
      const blob = new Blob([new Uint8Array(byteNumbers)], {type: 'application/octet-stream'});
      saveAs(blob, `factures-avoirs-cegid.tra`);
    });
  };

  onDateReceptionChange = (dates: Date[] | undefined): void => {
    if (!dates) {
      this.factureExterneSvc.filtersSignal.update(f => ({ ...f, dateReceptionDebut: null, dateReceptionFin: null, page: 0 }));
      return;
    }
    this.factureExterneSvc.filtersSignal.update(f => ({
      ...f,
      dateReceptionDebut: this.utilsSvc.formatToIsoDate(dates[0]),
      dateReceptionFin: this.utilsSvc.formatToIsoDate(dates[1]),
      page: 0
    }));
  };

  openDialog = (dto: FactureExterneDTO): void => {
    this.selectedFactureId.set(dto.id);
    this.selectedDialogTitle.set(dto.numero ? `Facture externe — ${dto.numero}` : 'Facture externe');
    this.showDialog.set(true);
  };

  onEditorPreparing = (e: any): void => {
    if (e.parentType !== 'filterRow') return;
    if (e.dataField === 'statut') {
      e.editorName = 'dxSelectBox';
      e.editorOptions = { ...e.editorOptions, dataSource: null, items: this.statutList, valueExpr: 'value', displayExpr: 'label', showClearButton: true };
    } else if (e.dataField === 'rapprochementStatut') {
      e.editorName = 'dxSelectBox';
      e.editorOptions = { ...e.editorOptions, dataSource: null, items: this.rapprochementStatutList, valueExpr: 'value', displayExpr: 'label', showClearButton: true };
    }
  };

  onSelectionChanged = (e: any): void => {
    const idList: number[] = (e.selectedRowsData as FactureExterneDTO[]).map(r => r.id);
    this.selectedFactureIdList.set(idList);
  };

  onGridOptionChanged = (e: any): void => {
    if (!e.fullName) return;

    if (e.fullName === 'paging.pageIndex') {
      this.factureExterneSvc.filtersSignal.update(f => ({ ...f, page: e.value }));
      return;
    }

    if (e.fullName === 'paging.pageSize') {
      this.factureExterneSvc.filtersSignal.update(f => ({ ...f, limit: e.value, page: 0 }));
      return;
    }

    if (!e.fullName.includes('filterValue')) return;
    if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
    this.filterDebounceTimer = setTimeout(() => {
      const numero = (this.grid().instance.columnOption('numero', 'filterValue') as string) || null;
      const siteExterneLibelle = (this.grid().instance.columnOption('siteExterneLibelle', 'filterValue') as string) || null;
      const numeroBonLivraison = (this.grid().instance.columnOption('numeroBonLivraison', 'filterValue') as string) || null;
      const statut = (this.grid().instance.columnOption('statut', 'filterValue') as string) || null;
      const rapprochementStatut = (this.grid().instance.columnOption('rapprochementStatut', 'filterValue') as string) || null;
      const dateFacture = this.utilsSvc.formatToIsoDate(this.grid().instance.columnOption('dateFacture', 'filterValue') as Date | null);
      const dateRapprochement = this.utilsSvc.formatToIsoDate(this.grid().instance.columnOption('dateRapprochement', 'filterValue') as Date | null);
      const avoirBloquantRaw = this.grid().instance.columnOption('avoirBloquant', 'filterValue');
      const avoirBloquant = avoirBloquantRaw === true || avoirBloquantRaw === false ? avoirBloquantRaw : null;
      void this.grid().instance.pageIndex(0);
      this.factureExterneSvc.filtersSignal.update(f => ({ ...f, numero, siteExterneLibelle, numeroBonLivraison, statut, rapprochementStatut, dateFacture, dateRapprochement, avoirBloquant, page: 0 }));
    }, 400);
  };
}
